import React from "react";
import {
  FocusContext,
  focusContent,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectDisableNavigation } from "../../redux/Slices/loading.slice";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";
import { hasAuthenticatedSession } from "../../services/authStorage";

/**
 * Sidebar Component
 * 
 * Legacy CSS: NO flexbox
 * Uses: display:table, table-cell, inline-block
 */

function sideElement(tag, className) {
  var SideElement = React.forwardRef(function SideElement(props, ref) {
    var forwardedProps = Object.assign({}, props);
    delete forwardedProps.focused;
    delete forwardedProps.isActive;
    var stateClass = (props.focused ? " is-focused" : "") + (props.isActive ? " is-active" : "");
    forwardedProps.className = className + stateClass + (props.className ? " " + props.className : "");
    forwardedProps.ref = ref;
    return React.createElement(tag, forwardedProps);
  });

  SideElement.displayName = "SideElement";
  return SideElement;
}

var MenuItemBox = sideElement("div", "legacy-sidebar-menu-item");
var MenuItemInner = sideElement("div", "legacy-sidebar-menu-item-inner");
var MenuItemContent = sideElement("div", "legacy-sidebar-menu-item-content");
var MenuIcon = sideElement("img", "legacy-sidebar-menu-icon");
var MenuIconMoftabakh = sideElement("img", "legacy-sidebar-menu-icon legacy-sidebar-menu-icon-moftabakh");
var MenuLabel = sideElement("span", "legacy-sidebar-menu-label");
var MenuLabelMoftabakh = sideElement("span", "legacy-sidebar-menu-label legacy-sidebar-menu-label-moftabakh");
var MenuWrapper = sideElement("div", "legacy-sidebar-wrapper");
var LogoContainer = sideElement("div", "legacy-sidebar-logo");
var LogoImage = sideElement("img", "legacy-sidebar-logo-image");

// Check if running on WebOS (LG TV has its own D-pad)
// Note: Tizen (Samsung) TVs still show the virtual D-pad
var isTV = function () {
  if (typeof window === "undefined") return false;
  var ua = navigator.userAgent.toLowerCase();
  return ua.includes("webos") || ua.includes("smarttv");
};

// D-pad controller - fixed position at bottom left
var DPadFixedWrapper = function DPadFixedWrapper(props) {
  return <div {...props} className="legacy-dpad-fixed" style={Object.assign({ display: isTV() ? "none" : "block" }, props.style || {})} />;
};
var DPadContainer = sideElement("div", "legacy-dpad-container");
var DPadIcon = sideElement("span", "legacy-dpad-icon");
var DPadUp = sideElement("button", "legacy-dpad-button legacy-dpad-up");
var DPadDown = sideElement("button", "legacy-dpad-button legacy-dpad-down");
var DPadLeft = sideElement("button", "legacy-dpad-button legacy-dpad-left");
var DPadRight = sideElement("button", "legacy-dpad-button legacy-dpad-right");
var DPadCenter = sideElement("button", "legacy-dpad-center");
var DPadCenterText = sideElement("span", "legacy-dpad-center-text");

function getMenuItemFocusKey(target) {
  if (target === "/") return "menu_home";
  return "menu_" + String(target || "").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

// Simulate remote keypress with old Chromium-safe event creation.
var createRemoteKeyEvent = function (eventName, keyCode) {
  var event;
  var keyByCode = {
    13: "Enter",
    37: "ArrowLeft",
    38: "ArrowUp",
    39: "ArrowRight",
    40: "ArrowDown",
  };

  try {
    event = new KeyboardEvent(eventName, {
      key: keyByCode[keyCode] || "",
      keyCode: keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true,
    });

    if (event.keyCode !== keyCode && Object.defineProperty) {
      Object.defineProperty(event, "keyCode", {
        get: function () {
          return keyCode;
        },
      });
      Object.defineProperty(event, "which", {
        get: function () {
          return keyCode;
        },
      });
    }
  } catch (error) {
    event = document.createEvent("Event");
    event.initEvent(eventName, true, true);
    event.keyCode = keyCode;
    event.which = keyCode;
    event.key = keyByCode[keyCode] || "";
  }

  return event;
};

var simulateKeypress = function (keyCode) {
  var keyDownEvent = createRemoteKeyEvent("keydown", keyCode);
  var keyUpEvent = createRemoteKeyEvent("keyup", keyCode);

  window.dispatchEvent(keyDownEvent);
  setTimeout(function () {
    window.dispatchEvent(keyUpEvent);
  }, 0);
};

// Menu item component with icon
function MenuItem({ children, target, icon, isMoftabakh, focusKey }) {
  var navigate = useNavigate();
  var pathname = window.location.pathname;
  var isNavigationDisabled = useSelector(selectDisableNavigation);

  var handleNavigation = function () {
    if (!isNavigationDisabled) {
      trackGoogleAnalyticsEvent("tv_navigation", {
        navigation_label: typeof children === "string" ? children : "",
        navigation_target: target,
        navigation_source: pathname,
      });
      navigate(target);
    }
  };

  var focusableResult = useFocusable({
    focusKey: focusKey,
    onEnterPress: handleNavigation,
    onArrowPress: function (direction) {
      if (direction === "left" && focusContent()) {
        return false;
      }
      return true;
    },
    focusable: !isNavigationDisabled,
  });

  var ref = focusableResult.ref;
  var focused = focusableResult.focused;

  // Determine if this item is the active route
  var isActive = false;
  if (target === "/") {
    isActive = pathname === "/";
  } else if (target === "/categories") {
    // Categories menu should be active for /categories, /category/*, and /genre/*
    isActive = pathname === "/categories" ||
      pathname.startsWith("/category/") ||
      pathname.startsWith("/genre/");
  } else {
    isActive = pathname.startsWith(target);
  }

  var isFocused = focused && !isNavigationDisabled;
  var IconComponent = isMoftabakh ? MenuIconMoftabakh : MenuIcon;
  var LabelComponent = isMoftabakh ? MenuLabelMoftabakh : MenuLabel;

  return (
    <MenuItemBox
      ref={ref}
      focused={isFocused}
      isActive={isActive && !isFocused}
      onClick={handleNavigation}
      style={{
        opacity: isNavigationDisabled ? 0.5 : 1,
        pointerEvents: isNavigationDisabled ? "none" : "auto",
        cursor: isNavigationDisabled ? "not-allowed" : "pointer",
      }}
    >
      <MenuItemInner>
        <MenuItemContent>
          {icon && <IconComponent src={icon} alt="" focused={isFocused} />}
          <LabelComponent focused={isFocused}>{children}</LabelComponent>
        </MenuItemContent>
      </MenuItemInner>
    </MenuItemBox>
  );
}

// D-Pad Component (fixed position, rendered outside sidebar)
// Hidden on player screen (/watch and /trailer routes) - player has its own dpad
export function DPadController() {
  // Hide on player pages - the player has its own dpad
  if (typeof window !== 'undefined' && (window.location.pathname.startsWith('/watch') || window.location.pathname.startsWith('/trailer'))) {
    return null;
  }

  return (
    <DPadFixedWrapper>
      <DPadContainer>
        <DPadUp onClick={function () { simulateKeypress(38); }}>
          <DPadIcon>▲</DPadIcon>
        </DPadUp>
        <DPadRight onClick={function () { simulateKeypress(39); }}>
          <DPadIcon>▶</DPadIcon>
        </DPadRight>
        <DPadCenter onClick={function () { simulateKeypress(13); }}>
          <DPadCenterText>OK</DPadCenterText>
        </DPadCenter>
        <DPadLeft onClick={function () { simulateKeypress(37); }}>
          <DPadIcon>◀</DPadIcon>
        </DPadLeft>
        <DPadDown onClick={function () { simulateKeypress(40); }}>
          <DPadIcon>▼</DPadIcon>
        </DPadDown>
      </DPadContainer>
    </DPadFixedWrapper>
  );
}

// Main Menu export
export function Menu({ focusKey: focusKeyParam }) {
  var token = hasAuthenticatedSession();
  // Menu items matching Android TV sidebar
  var menuItems = [
    { label: "صفحه اصلی", url: "/", icon: "/icons/home.svg" },
    { label: "جستجو", url: "/search", icon: "/icons/search.svg" },
    { label: "ژانر و دسته بندی", url: "/categories", icon: "/icons/category.svg" },
    { label: "فیلم", url: "/movie", icon: "/icons/movie.svg" },
    { label: "سریال", url: "/series", icon: "/icons/series.svg" },
    { label: "موسیقی", url: "/music", icon: "/icons/song.svg" },
    { label: "آناوطن", url: "/anavatan", icon: "/icons/iran.svg" },
    { label: "مفته باخ", url: "/moftabakh", icon: "/icons/play.svg", isMoftabakh: true },
    { label: token ? "حساب کاربری" : "ورود / ثبت نام", url: token ? "/profile" : "/login", icon: "/icons/user.svg" },
  ];
  var pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  var activeMenuFocusKey = getMenuItemFocusKey("/");

  for (var menuIndex = 0; menuIndex < menuItems.length; menuIndex += 1) {
    var item = menuItems[menuIndex];
    var isActive = false;
    if (item.url === "/") {
      isActive = pathname === "/";
    } else if (item.url === "/categories") {
      isActive =
        pathname === "/categories" ||
        pathname.indexOf("/category/") === 0 ||
        pathname.indexOf("/genre/") === 0;
    } else {
      isActive = pathname.indexOf(item.url) === 0;
    }

    if (isActive) {
      activeMenuFocusKey = getMenuItemFocusKey(item.url);
      break;
    }
  }

  var focusableResult = useFocusable({
    focusable: false,
    saveLastFocusedChild: true,
    trackChildren: true,
    autoRestoreFocus: true,
    focusKey: focusKeyParam,
    preferredChildFocusKey: activeMenuFocusKey,
    isFocusBoundary: true,
    focusBoundaryDirections: ["right"],
  });

  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  return (
    <FocusContext.Provider value={focusKey}>
      <MenuWrapper ref={ref}>
        <LogoContainer>
          <Link to="/">
            <LogoImage src="/images/logo.svg" alt="Manabakh" />
          </Link>
        </LogoContainer>

        {menuItems.map(function (item) {
          return (
            <MenuItem
              key={"side_" + item.url}
              target={item.url}
              icon={item.icon}
              isMoftabakh={item.isMoftabakh}
              focusKey={getMenuItemFocusKey(item.url)}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </MenuWrapper>
    </FocusContext.Provider>
  );
}

// Legacy Sidebar component (not used but kept for exports)
var Sidebar = function () {
  return null;
};

export default Sidebar;
